import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLeads, fetchLeadStats, deleteLead } from '../../store/slices/leadSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import LeadModal from './LeadModal';
import LeadDetailModal from './LeadDetailModal';
import { LEAD_STATUS } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const SalesDashboard = () => {
  const dispatch = useDispatch();
  const { leads, stats, pagination, loading } = useSelector((state) => state.leads);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  useEffect(() => {
    dispatch(fetchLeads({ page, ...filters }));
    dispatch(fetchLeadStats());
  }, [dispatch, page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await dispatch(deleteLead(id)).unwrap();
      } catch (error) {
        alert('Failed to delete lead: ' + error);
      }
    }
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Sales Dashboard</h1>
          <button
            className="btn btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <i className="ti ti-plus me-1" />
            Create Lead
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Leads</h3>
              <p className="stat-value">{stats.total}</p>
            </div>
            {Object.entries(stats.by_status || {}).map(([status, count]) => (
              <div key={status} className="stat-card">
                <h3><StatusBadge status={status} /></h3>
                <p className="stat-value">{count}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {Object.values(LEAD_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search by name, email, or company"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        No leads found
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr key={lead._id} className="align-middle">
                        <td className="fw-medium">{lead.name}</td>
                        <td>{lead.company}</td>
                        <td>{lead.email}</td>
                        <td>{lead.phone}</td>
                        <td><StatusBadge status={lead.status} /></td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            {(Array.isArray(lead.assigned_to) ? lead.assigned_to : [lead.assigned_to]).filter(Boolean).map((emp, idx) => (
                              <span key={idx} className="badge bg-primary" style={{ fontSize: '11px' }}>
                                {emp.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{formatDateTime(lead.created_at)}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setSelectedLeadId(lead._id)}
                            >
                              View
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(lead._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <LeadModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            dispatch(fetchLeads({ page, ...filters }));
            dispatch(fetchLeadStats());
          }}
        />
      )}

      {selectedLeadId && (
        <LeadDetailModal
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          onSuccess={() => {
            dispatch(fetchLeads({ page, ...filters }));
            dispatch(fetchLeadStats());
          }}
        />
      )}
    </Layout>
  );
};

export default SalesDashboard;
